import React, { useEffect, useState } from "react";
import DragDropProject from "./components/DragDropProject";
import MessagePopup from "./components/toasts/MessagePopup";
import { decryptToken, decryptDate } from "./utility/licenseToken";



function AppURL({ widget }) {
  // "checking" | "authorized" | "expired" | "invalid" | "url_mismatch"
  const [status, setStatus] = useState("checking");
  const [expiryDate, setExpiryDate] = useState(null);

  useEffect(() => {
    let token = null;

    // ── Source 1: window.location.search
    //    Works when opening index.html directly in a browser (local dev / testing)
    const params = new URLSearchParams(window.location.search);
    token = params.get("key");
    if (!token && window.location.search) {
      // If there's no ?key=... try reading the entire query string as the token
      // e.g. ?QgNaQgNjdnFncA.. (window.location.search will be "?QgNaQgNjdnFncA..")
      const rawSearch = window.location.search.replace(/^\?/, "");
      if (rawSearch && !rawSearch.includes("=")) {
        token = rawSearch;
      }
    }

    // ── Source 2: widget.url  ← the 3DEXPERIENCE "Source code URL" field
    //    The platform strips query params from window.location but they survive
    //    in widget.url, which is admin-controlled — users CANNOT change it.
    if (!token && widget) {
      try {
        const srcUrl = widget.url || widget.getUrl?.();
        if (srcUrl) {
          const parsedUrl = new URL(srcUrl);
          const srcParams = new URLSearchParams(parsedUrl.search);
          token = srcParams.get("key");
          if (!token && parsedUrl.search) {
            const rawSrcSearch = parsedUrl.search.replace(/^\?/, "");
            if (rawSrcSearch && !rawSrcSearch.includes("=")) {
              token = rawSrcSearch;
            }
          }
        }
      } catch {
        token = null;
      }
    }

    // ── Source 3: URL hash  e.g. index.html#key=TOKEN or index.html#TOKEN (bonus fallback)
    if (!token && window.location.hash) {
      const rawHash = window.location.hash.replace(/^#/, "");
      const hashParams = new URLSearchParams(rawHash);
      token = hashParams.get("key");
      if (!token && rawHash && !rawHash.includes("=")) {
        token = rawHash;
      }
    }

    if (!token) {
      setStatus("invalid");
      return;
    }

    // ── NEW: Try combined URL+Date token first (decryptToken)
    const combined = decryptToken(token);

    if (combined) {
      const { baseUrl: tokenBaseUrl, expiryDate: tokenExpiry } = combined;

      // Compare the token's base URL against the runtime origin (tenant domain).
      // We only check the origin (scheme + host), NOT the full path —
      // so a token for "https://msildev.brainwaveconsulting.co.in/" is valid
      // for ANY widget deployed under that tenant, regardless of path.
      const normalise = (u) => u.toLowerCase().replace(/\/+$/, "");

      let tokenOrigin;
      try {
        tokenOrigin = new URL(tokenBaseUrl).origin;
      } catch {
        setStatus("url_mismatch");
        return;
      }

      const runtimeOrigin = window.location.origin;
      const urlMatch = normalise(runtimeOrigin) === normalise(tokenOrigin);

      if (!urlMatch) {
        setStatus("url_mismatch");
        return;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateValid = today <= new Date(tokenExpiry);

      setExpiryDate(tokenExpiry);
      setStatus(dateValid ? "authorized" : "expired");
      return;
    }

    // ── Fallback: legacy date-only token (decryptDate)
    const decoded = decryptDate(token);

    if (!decoded) {
      setStatus("invalid");
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateValid = today <= new Date(decoded);

    setExpiryDate(decoded);
    setStatus(dateValid ? "authorized" : "expired");
  }, [widget]);


  /* ── Loading ────────────────────────────────────────────── */
  if (status === "checking") {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
          background: "#f4f5f7",
          color: "#77797c",
          fontSize: 15,
          gap: 16,
        }}
      >
        <span
          style={{
            width: 32,
            height: 32,
            border: "3px solid rgba(0, 86, 179, 0.1)",
            borderTopColor: "#0056b3",
            borderRadius: "50%",
            display: "inline-block",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <span style={{ fontWeight: 500, color: "#3d4247" }}>
          Verifying security signature…
        </span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  /* ── Authorized ─────────────────────────────────────────── */
  if (status === "authorized") {
    return (
      <div style={{ textAlign: "center", justifyContent: "left" }}>
        <MessagePopup />
        <DragDropProject widget={widget} />
      </div>
    );
  }

  /* ── Blocked screens ────────────────────────────────────── */
  const MESSAGES = {
    expired: {
      icon: "⏰",
      iconColor: "#d9534f",
      iconBg: "rgba(217, 83, 79, 0.06)",
      glowColor: "rgba(217, 83, 79, 0.12)",
      badge: "EXPIRED LICENSE",
      badgeColor: "#c9302c",
      badgeBg: "rgba(217, 83, 79, 0.08)",
      title: "License Terminus Reached",
      body: expiryDate
        ? `Your organization's authorized access token expired on ${expiryDate}.`
        : "Your organization's authorized access token has expired.",
      sub: "Please coordinate with your IT administrator to renew the cryptographic license key for this widget.",
    },
    invalid: {
      icon: "🔒",
      iconColor: "#0056b3",
      iconBg: "rgba(0, 86, 179, 0.06)",
      glowColor: "rgba(0, 86, 179, 0.12)",
      badge: "UNAUTHORIZED ACCESS",
      badgeColor: "#0056b3",
      badgeBg: "rgba(0, 86, 179, 0.08)",
      title: "Authentication Token Required",
      body: "A valid secure access signature is required to load the Pert Chart Widget.",
      sub: "For support contact: sales@bwclabs.com"
    },
    url_mismatch: {
      icon: "🚫",
      iconColor: "#e67e22",
      iconBg: "rgba(230, 126, 34, 0.06)",
      glowColor: "rgba(230, 126, 34, 0.12)",
      badge: "DOMAIN MISMATCH",
      badgeColor: "#c0392b",
      badgeBg: "rgba(230, 126, 34, 0.08)",
      title: "Token Domain Mismatch",
      body: "This license token was issued for a different deployment URL and cannot be used here.",
      sub: "Please generate a new token using the correct base URL for this deployment and update the Source Code URL in the portal.",
    },
  };

  const msg = MESSAGES[status] ?? MESSAGES.invalid;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Segoe UI', Roboto, sans-serif",
        background: "linear-gradient(145deg, #e6ecf5 0%, #d8e2ee 100%)",
        padding: "24px",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        style={{
          zIndex: 2,
          position: "relative",
          background: "rgba(255, 255, 255, 0.45)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          borderRadius: 24,
          padding: "48px 40px",
          maxWidth: 460,
          width: "100%",
          boxShadow: "0 20px 45px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.7)",
          textAlign: "center",
          transform: "translateY(0px)",
          animation: "float 6s ease-in-out infinite",
        }}
      >
        {/* Top Icon / Visual Badge */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: msg.iconBg,
            color: msg.iconColor,
            fontSize: 32,
            marginBottom: 24,
            border: "1px solid rgba(255, 255, 255, 0.8)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.02)",
          }}
        >
          {msg.icon}
        </div>

        <div style={{ marginBottom: 16 }}>
          <span
            style={{
              display: "inline-block",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.1em",
              color: msg.badgeColor,
              background: msg.badgeBg,
              padding: "4px 12px",
              borderRadius: 4,
              border: `1px solid rgba(${status === "expired" ? "217, 83, 79" : "0, 86, 179"}, 0.2)`,
            }}
          >
            {msg.badge}
          </span>
        </div>

        {/* Heading & Details */}
        <h2
          style={{
            margin: "0 0 16px",
            fontSize: 22,
            fontWeight: 600,
            color: "#1f252b",
            lineHeight: 1.3,
          }}
        >
          {msg.title}
        </h2>

        <p
          style={{
            margin: "0 0 24px",
            fontSize: 14.5,
            lineHeight: 1.6,
            color: "#5c646d",
          }}
        >
          {msg.body}
        </p>

        {/* Bottom Helper Info */}
        <div
          style={{
            paddingTop: 20,
            borderTop: "1px solid #e9edf4",
            fontSize: 12.5,
            color: "#7e8b9b",
            lineHeight: 1.5,
          }}
        >
          {msg.sub}
        </div>
      </div>

      {/* Styled Micro-animations */}
      <style>{`
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
          100% { transform: translateY(0px); }
        }
        @keyframes pulse-blob {
          0% { transform: scale(0.9) translate(0px, 0px); opacity: 0.8; }
          50% { transform: scale(1.1) translate(10px, -15px); opacity: 1; }
          100% { transform: scale(0.95) translate(-10px, 15px); opacity: 0.85; }
        }
      `}</style>
    </div>
  );
}

export default AppURL;
